import React, { useState } from 'react';
import { CreateAppointmentData } from '../types/appointment';
import { Patient } from '../types/patient';
import { DateInput } from './forms';
import { appointmentMinDate } from './forms/appointmentMinDate';
import { PatientSearch } from './PatientSearch';
import { ProviderSearch, Provider } from './ProviderSearch';
import { useOrganizationStore } from '../stores/organizationStore';

interface AppointmentFormProps {
  onSubmit: (data: CreateAppointmentData) => void;
  onCancel: () => void;
  loading?: boolean;
  initialData?: Partial<CreateAppointmentData>;
}

export const AppointmentForm: React.FC<AppointmentFormProps> = ({
  onSubmit,
  onCancel,
  loading = false,
  initialData = {}
}) => {
  const [formData, setFormData] = useState<CreateAppointmentData>({
    patientId: initialData.patientId || '',
    providerId: initialData.providerId || '',
    appointmentDetails: {
      scheduledDate: initialData.appointmentDetails?.scheduledDate || '',
      type: initialData.appointmentDetails?.type || 'consultation',
      location: initialData.appointmentDetails?.location || '',
      chiefComplaint: initialData.appointmentDetails?.chiefComplaint || ''
    }
  });

  // Someone who does not record (a standalone admin, AMP-137) books for a provider, so the
  // provider cannot be left blank: blank means "assign to yourself".
  const mustChooseProvider = useOrganizationStore((s) => s.canRecord) === false;

  const [validationState, setValidationState] = useState({
    scheduledDate: true
  });

  const appointmentTypes = [
    { value: 'consultation', label: 'Consultation', description: 'Initial or routine consultation' },
    { value: 'follow-up', label: 'Follow-up', description: 'Follow-up appointment' },
    { value: 'procedure', label: 'Procedure', description: 'Medical procedure' },
    { value: 'emergency', label: 'Emergency', description: 'Emergency visit' },
    { value: 'other', label: 'Other', description: 'Other type of appointment' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.patientId) {
      alert('Please select a patient');
      return;
    }

    if (mustChooseProvider && !formData.providerId) {
      alert('Please choose the provider for this appointment');
      return;
    }

    if (!formData.appointmentDetails.scheduledDate) {
      alert('Please select a scheduled date');
      return;
    }

    if (!formData.appointmentDetails.location) {
      alert('Please enter a location');
      return;
    }

    if (!validationState.scheduledDate) {
      alert('Please fix validation errors before submitting');
      return;
    }

    onSubmit(formData);
  };

  const handlePatientSelect = (patient: Patient) => {
    setFormData(prev => ({
      ...prev,
      patientId: patient?.id || ''
    }));
  };

  const handleProviderSelect = (provider: Provider | null) => {
    setFormData(prev => ({
      ...prev,
      providerId: provider?.id || ''
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Patient Selection */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Patient Information</h3>
        
        <PatientSearch
          selectedPatientId={formData.patientId}
          onSelectPatient={handlePatientSelect}
          label="Patient"
          required={true}
        />
      </div>

      {/* Provider Selection */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Provider Information</h3>
        
        <ProviderSearch
          selectedProviderId={formData.providerId}
          onSelectProvider={handleProviderSelect}
          label="Provider"
          required={mustChooseProvider}
          placeholder={mustChooseProvider
            ? 'Search for the provider who will see the patient...'
            : 'Search for a provider or leave blank to assign to yourself...'}
        />
      </div>

      {/* Appointment Details */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Appointment Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Scheduled Date & Time */}
          <div className="md:col-span-2">
            <DateInput
              label="Scheduled Date & Time"
              value={formData.appointmentDetails.scheduledDate}
              onChange={(value) => setFormData(prev => ({
                ...prev,
                appointmentDetails: {
                  ...prev.appointmentDetails,
                  scheduledDate: value
                }
              }))}
              onValidationChange={(isValid) => setValidationState(prev => ({ ...prev, scheduledDate: isValid }))}
              required={true}
              showTime={true}
              minDate={appointmentMinDate()}
            />
          </div>

          {/* Appointment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Appointment Type <span className="text-red-500">*</span>
            </label>
            <select
              className="input-field"
              value={formData.appointmentDetails.type}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                appointmentDetails: {
                  ...prev.appointmentDetails,
                  type: e.target.value as CreateAppointmentData['appointmentDetails']['type']
                }
              }))}
              required
            >
              {appointmentTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {appointmentTypes.find(t => t.value === formData.appointmentDetails.type)?.description}
            </p>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="e.g., Room 101, Clinic A"
              value={formData.appointmentDetails.location}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                appointmentDetails: {
                  ...prev.appointmentDetails,
                  location: e.target.value
                }
              }))}
              required
            />
          </div>

          {/* Chief Complaint */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chief Complaint (Optional)
            </label>
            <textarea
              className="input-field resize-none"
              rows={3}
              placeholder="Brief description of the patient's main concern..."
              value={formData.appointmentDetails.chiefComplaint || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                appointmentDetails: {
                  ...prev.appointmentDetails,
                  chiefComplaint: e.target.value
                }
              }))}
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1">
              {(formData.appointmentDetails.chiefComplaint || '').length}/500 characters
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Appointment'}
        </button>
      </div>
    </form>
  );
};